import { getApiBaseUrl } from '../config/api';

export interface CaregiverProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  bio: string | null;
  phoneNumber: string | null;
  location: string | null;
  yearsExperience: number | null;
  hourlyRate: number | null;
  responseTime: string | null;
  languages: string[];
  profileComplete: boolean;
  // FEAT-047 — per-state agency register/verify link inputs (MVP-8.16,
  // 8.17, 8.18, 8.35a/b/c).
  stateCode: string | null;
  stateLicenseNumber: string | null;
  services: Service[];
  availability: Availability[];
  certifications: Certification[];
  specialties: Specialty[];
}

export interface Service {
  id: string;
  name: string;
  description: string | null;
  duration: number | null;
  price: number | string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Availability {
  id: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string | null;
  expiryDate: string | null;
  credentialId: string | null;
  verified: boolean;
  documentUrl: string | null;
  documentName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Specialty {
  id: string;
  name: string;
  description: string | null;
}

export interface SkillDefinition {
  id: string;
  label: string;
  category: string;
  gated: boolean;
}

export interface SkillClaim {
  skillId: string;
  category: string;
  label: string;
  gated: boolean;
}

export interface SkillClaimsUpdateResult {
  ok: boolean;
  validation: {
    allowed: string[];
    blocked: Array<{ skillId: string; label: string; guidance: string }>;
  };
}

export interface CreateServiceData {
  name: string;
  description: string;
  duration: number;
  price: number;
}

export interface UpdateServiceData {
  name?: string;
  description?: string;
  duration?: number;
  price?: number;
  isActive?: boolean;
}

export interface CreateAvailabilityData {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

export interface UpdateAvailabilityData {
  startTime?: string;
  endTime?: string;
  isAvailable?: boolean;
}

export interface CreateCertificationData {
  name: string;
  issuer: string;
  issueDate?: string;
  expiryDate?: string;
  credentialId?: string;
  documentUrl?: string;
  documentName?: string;
}

export interface UpdateCertificationData {
  name?: string;
  issuer?: string;
  issueDate?: string;
  expiryDate?: string;
  credentialId?: string;
  documentUrl?: string;
  documentName?: string;
  verified?: boolean;
}

export interface UpdateProfileData {
  bio?: string;
  phoneNumber?: string;
  location?: string;
  yearsExperience?: number;
  hourlyRate?: number;
  responseTime?: string;
  languages?: string[];
  // FEAT-045 — stepper credentials step writes the state license
  // number + two-letter state code through this endpoint. Both fields
  // round-trip through the existing PUT /caregiver-profile/profile
  // handler so we don't introduce a sibling route.
  stateLicenseNumber?: string;
  stateCode?: string;
}

class CaregiverProfileService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async getProfile(): Promise<CaregiverProfile> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/profile`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch profile');
    }

    const result = await response.json();
    return result.data;
  }

  async updateBio(bio: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/bio`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ bio }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update bio');
    }
  }

  async updateProfile(data: UpdateProfileData): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/profile`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update profile');
    }
  }

  async updateProfileCompletion(profileComplete: boolean): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/profile-completion`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ profileComplete }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update profile completion');
    }
  }

  // Services
  async getServices(): Promise<Service[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/services`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch services');
    }

    const result = await response.json();
    return result.data;
  }

  async createService(data: CreateServiceData): Promise<Service> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/services`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create service');
    }

    const result = await response.json();
    return result.data;
  }

  async updateService(serviceId: string, data: UpdateServiceData): Promise<Service> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/services/${serviceId}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update service');
    }

    const result = await response.json();
    return result.data;
  }

  async deleteService(serviceId: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/services/${serviceId}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete service');
    }
  }

  // Availability
  async getAvailability(): Promise<Availability[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/availability`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch availability');
    }

    const result = await response.json();
    return result.data;
  }

  async createAvailability(data: CreateAvailabilityData): Promise<Availability> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/availability`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create availability');
    }

    const result = await response.json();
    return result.data;
  }

  async updateAvailability(availabilityId: string, data: UpdateAvailabilityData): Promise<Availability> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/availability/${availabilityId}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update availability');
    }

    const result = await response.json();
    return result.data;
  }

  async deleteAvailability(availabilityId: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/availability/${availabilityId}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete availability');
    }
  }

  async bulkUpdateAvailability(availability: CreateAvailabilityData[]): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/availability/bulk`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ availability }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update availability');
    }
  }

  // Certifications
  async getCertifications(): Promise<Certification[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/certifications`, {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch certifications');
    }

    const result = await response.json();
    return result.data;
  }

  async createCertification(data: CreateCertificationData): Promise<Certification> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/certifications`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create certification');
    }

    const result = await response.json();
    return result.data;
  }

  async updateCertification(certificationId: string, data: UpdateCertificationData): Promise<Certification> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/certifications/${certificationId}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update certification');
    }

    const result = await response.json();
    return result.data;
  }

  async deleteCertification(certificationId: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/certifications/${certificationId}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete certification');
    }
  }

  async getSkillTaxonomy(): Promise<SkillDefinition[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/skills/taxonomy`, {
      headers: await this.getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to load skill taxonomy');
    }
    const body = await response.json();
    return body.skills;
  }

  async getSkillClaims(): Promise<SkillClaim[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/skills`, {
      headers: await this.getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to load skill claims');
    }
    const body = await response.json();
    return body.claims;
  }

  async updateSkillClaims(skillIds: string[]): Promise<SkillClaimsUpdateResult> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregiver-profile/skills`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ skillIds }),
    });
    const body = await response.json();
    if (response.status === 422) {
      return { ok: false, validation: body.validation };
    }
    if (!response.ok) {
      throw new Error(body.message ?? 'Failed to update skill claims');
    }
    return { ok: true, validation: body.validation };
  }
}

export const caregiverProfileService = new CaregiverProfileService();
